/**
 * Prop possession: a hider takes the shape of a placed prop.
 *
 * ============================================================================
 * THIS FILE RUNS UNMODIFIED ON BOTH THE CLIENT AND THE SERVER.
 * ============================================================================
 *
 * Same discipline as movement.ts and round.ts, and for the same reason. A
 * possessed hider moves with the *prop's* hull, so every rule about which prop
 * you may take and whether it fits where you stand is simulation input. If the
 * two sides disagree about any of it, the hider's collision box differs from
 * the one the server is resolving against and they rubber-band continuously —
 * a far worse version of the phase-gate desync, because a hull disagreement
 * does not converge on its own.
 *
 * What lives here is only the *decision*. Suppressing the possessed prop's
 * solid, swapping the hull, broadcasting the result: all side effects, all
 * owned by the caller. Exactly the split `nextPhase` uses.
 *
 * ---------------------------------------------------------------------------
 * What possession does and does not hide
 * ---------------------------------------------------------------------------
 *
 * Worth being precise, because it is easy to assume this buys more than it
 * does. Every client has the map. So every client can compare a prop's live
 * position against its authored one, and a prop that has moved is a player —
 * no packet inspection required, and nothing the server sends or withholds can
 * change that. **A hider who moves has been seen.** That is the game, and
 * `EntFlag.Moving` exists to say so out loud.
 *
 * What the server can and must protect is the *identity* mapping: a hunter's
 * socket must never carry "player 3 is a hider disguised as crate #7". That is
 * not observable by playing, it is readable straight out of a WebSocket frame,
 * and it turns the whole game off. Hence `propEntityId` below: to a hunter, a
 * disguised hider is published under the identity of the prop instance they
 * are wearing, and their player id never appears.
 *
 * The remaining gap, which this commit does *not* close: a modified client can
 * still tell which prop instances are possessed, because only possessed ones
 * are named in `Msg.Possess` and only their solids are suppressed. Closing it
 * means every static prop travelling in every snapshot so that a *stationary*
 * hider is byte-identical to scenery — about 21 kB/s per client on depot,
 * against 1.19 today, plus driving the renderer's instanced batches from
 * entity poses rather than from map data. That is its own change; see the M3
 * section of the README.
 */

import type { GameMap, PropCatalogue } from "./mapfmt.js";
import { DEFAULT_HULL, hullFits, type Hull } from "./movement.js";
import { POSSESS_RANGE } from "./rules.js";
import type { StaticWorld } from "./world.js";

/**
 * "Not possessing anything", on the wire and in memory.
 *
 * A sentinel rather than `null` because it crosses the wire as a u16 and
 * `mapfmt` prop indices are dense from zero, so there is no in-band value
 * spare. 0xffff is far above any plausible prop count and is what an all-ones
 * read of a truncated frame would produce, which fails closed.
 */
export const NO_PROP = 0xffff;

/**
 * Where prop-instance entity ids start, above every possible player id.
 *
 * Snapshot entity ids are a u8 shared between players (1..MAX_PLAYERS) and, in
 * a hunter's view, prop instances. 64 leaves room for MAX_PLAYERS to grow by a
 * lot before it collides, and still fits depot's 101 props under 255.
 */
export const PROP_ENTITY_ID_BASE = 64;

export function propEntityId(propIndex: number): number {
  return PROP_ENTITY_ID_BASE + propIndex;
}

export function isPropEntityId(id: number): boolean {
  return id >= PROP_ENTITY_ID_BASE;
}

export function propIndexFromEntityId(id: number): number {
  return id - PROP_ENTITY_ID_BASE;
}

/**
 * Index into `StaticWorld.solids` of the solid a placed prop contributes, or
 * `-1` if that prop contributes none.
 *
 * This reproduces `mapSolids`' documented ordering — every authored solid
 * first, then every *catalogued* prop in authored order — and it has to
 * reproduce the skip rule exactly. A prop whose id is not in the catalogue
 * produces no solid, so it does not advance the index either; get that wrong
 * and every prop after the first uncatalogued one suppresses its neighbour's
 * box instead of its own. Collision would then break somewhere the player is
 * not standing, which is close to undiagnosable from the symptom.
 *
 * `renderBatches` in the client reproduces the same skip rule for the same
 * reason. Both are asserted directly against `mapSolids` rather than trusted.
 */
export function propSolidIndex(
  map: GameMap,
  catalogue: PropCatalogue,
  propIndex: number,
): number {
  const target = map.props[propIndex];
  if (target === undefined || !catalogue.has(target.prop)) return -1;

  let offset = 0;
  for (let i = 0; i < propIndex; i++) {
    if (catalogue.has(map.props[i]!.prop)) offset += 1;
  }
  return map.solids.length + offset;
}

/** The hull a player takes on while wearing this prop, or `null` if unknown. */
export function propHull(
  map: GameMap,
  catalogue: PropCatalogue,
  propIndex: number,
): Hull | null {
  const placed = map.props[propIndex];
  if (placed === undefined) return null;
  return catalogue.get(placed.prop)?.hull ?? null;
}

/** The catalogue id a possessed player is drawn as. 0 means undisguised. */
export function propKindId(map: GameMap, propIndex: number): number {
  return map.props[propIndex]?.prop ?? 0;
}

/**
 * The prop instance a player standing here may take, or `null`.
 *
 * Nearest eligible within `POSSESS_RANGE`, measured horizontally from the
 * prop's *authored* position — not from where a possessed hider has carried
 * it, because an unpossessed prop is the only kind that can be taken and those
 * never move.
 *
 * Ties break on the lower index, and the comparison is written `<` so the
 * first of two equidistant props wins. Both details matter: this runs on two
 * machines that must reach the same answer without talking, so "nearest" has
 * to be a total order, not merely a minimum. Distances are compared squared to
 * keep a `Math.sqrt` out of a decision that only ever needs the ordering.
 */
export function possessionTarget(
  map: GameMap,
  catalogue: PropCatalogue,
  x: number,
  z: number,
  taken: ReadonlySet<number>,
): number | null {
  const maxDistSq = POSSESS_RANGE * POSSESS_RANGE;
  let best: number | null = null;
  let bestDistSq = Infinity;

  for (let i = 0; i < map.props.length; i++) {
    if (taken.has(i)) continue;
    const p = map.props[i]!;
    if (!catalogue.has(p.prop)) continue;

    const dx = p.x - x;
    const dz = p.z - z;
    const distSq = dx * dx + dz * dz;
    if (distSq > maxDistSq) continue;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = i;
    }
  }
  return best;
}

/**
 * What a possession attempt did.
 *
 * `Refused` is not an error — a hider mashing E in a corridor with nothing
 * nearby is the common case, and it must cost nothing and log nothing.
 */
export const PossessOutcome = {
  Refused: 0,
  Possessed: 1,
  Released: 2,
} as const;

export type PossessOutcomeId = (typeof PossessOutcome)[keyof typeof PossessOutcome];

export interface PossessResult {
  outcome: PossessOutcomeId;
  /** The prop now worn, or NO_PROP. Only meaningful when not Refused. */
  propIndex: number;
  hull: Hull;
}

/**
 * Resolve one press of Use for a player standing at (x, y, z).
 *
 * Wearing something already means this is a release; otherwise it is an
 * attempt to take the nearest prop. Either way the *new* hull has to fit where
 * the player is standing, which is the whole reason this returns a decision
 * rather than just picking a prop:
 *
 *  - Taking a taller or wider prop while tucked under a shelf would otherwise
 *    put the player's new box inside solid geometry, and axis-separated
 *    resolution pushes an already-overlapping hull out along whichever axis
 *    frees it first. That is a free teleport through a wall, and it is exactly
 *    the "swapping to a larger prop inside a wall is a free noclip" case
 *    `hullFits` was written for.
 *  - Releasing is the same problem mirrored: a crate can sit where a standing
 *    player cannot, so dropping the disguise in a crawlspace has to be refused
 *    or the player pops through the ceiling.
 *
 * `world` must already have the candidate's own solid suppressed for a
 * possession, and must still have it suppressed for a release — in both cases
 * the box being tested is the one the player would occupy, and the prop they
 * are wearing is not an obstacle to themselves. The caller owns that ordering
 * because it owns the suppression; `lobby.ts` does it in one place.
 */
export function resolveUse(
  map: GameMap,
  catalogue: PropCatalogue,
  world: StaticWorld,
  state: { x: number; y: number; z: number },
  current: number,
  taken: ReadonlySet<number>,
  scratch: number[] = [],
): PossessResult {
  if (current !== NO_PROP) {
    const fits = hullFits(world, DEFAULT_HULL, state.x, state.y, state.z, scratch);
    if (!fits) return { outcome: PossessOutcome.Refused, propIndex: current, hull: DEFAULT_HULL };
    return { outcome: PossessOutcome.Released, propIndex: NO_PROP, hull: DEFAULT_HULL };
  }

  const target = possessionTarget(map, catalogue, state.x, state.z, taken);
  if (target === null) {
    return { outcome: PossessOutcome.Refused, propIndex: NO_PROP, hull: DEFAULT_HULL };
  }

  const hull = propHull(map, catalogue, target);
  if (hull === null) {
    return { outcome: PossessOutcome.Refused, propIndex: NO_PROP, hull: DEFAULT_HULL };
  }
  if (!hullFits(world, hull, state.x, state.y, state.z, scratch)) {
    return { outcome: PossessOutcome.Refused, propIndex: NO_PROP, hull: DEFAULT_HULL };
  }
  return { outcome: PossessOutcome.Possessed, propIndex: target, hull };
}
